import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { isApiError } from '@/js/libs/api';
import dayjs from '@/js/libs/dayjs';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useNotification } from '@/js/libs/ui/src/V2';

import type { RequestErrorNotification } from 'src/hooks/useRequestErrorNotification.ts';
import { useRequestErrorNotification } from 'src/hooks/useRequestErrorNotification.ts';

const MailingPositionErrorSchema = z.object({
  sortOrder: z.number(),
  positionName: z.string(),
  reservedByOrderId: z.number().optional(),
  translationKey: z.string(),
  reservedUnitIds: z.array(z.string()).optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});

const MailingPositionsReconciliationErrorSchema = z.object({
  data: z.object({
    mailingPositionErrors: z.array(MailingPositionErrorSchema).optional(),
  }),
  status: z.number().optional(),
});

const DELIVERY_UNITS_RESERVED_KEY = 'DELIVERY_UNITS_RESERVED';
const BACKEND_DATE_FORMAT = 'DD.MM.YYYY';
const DISPLAY_DATE_FORMAT = 'D MMMM YYYY';

const formatDate = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const parsed = dayjs(value, BACKEND_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.format(DISPLAY_DATE_FORMAT) : value;
};

export const formatReservedDateRange = (dateFrom?: string, dateTo?: string) => {
  const from = formatDate(dateFrom);
  const to = formatDate(dateTo);

  if (from && to) {
    return `${from} – ${to}`;
  }

  return from ?? to ?? '';
};

type DeliveryUnitsReservedHandler = (sortOrder: number, reservedUnitIds: string[]) => void;

type MailingPositionErrorsNotifier = (error: unknown, onDeliveryUnitsReserved?: DeliveryUnitsReservedHandler) => boolean;

/**
 * Shows one notification per failed mailing position for endpoints that reconcile positions with Promopost
 * (`PUT /post-campaigns/{id}`, `PATCH /post-campaigns/{id}/accept`, `POST .../reconcile-mailing-positions`).
 * @returns A function that notifies about `mailingPositionErrors` and reports whether the error was of that shape.
 */
export const useMailingPositionErrorsNotification = (): MailingPositionErrorsNotifier => {
  const [t] = useTranslation(['postBookingTool']);
  const notify = useNotification();

  return useCallbackRef<MailingPositionErrorsNotifier>((error, onDeliveryUnitsReserved) => {
    if (!isApiError(error, MailingPositionsReconciliationErrorSchema)) {
      return false;
    }

    const mailingPositionErrors = error.response?.data.mailingPositionErrors;

    if (!mailingPositionErrors?.length) {
      return false;
    }

    mailingPositionErrors.forEach(({ sortOrder, positionName, reservedByOrderId, translationKey, reservedUnitIds, dateFrom, dateTo }) => {
      if (translationKey === DELIVERY_UNITS_RESERVED_KEY) {
        if (reservedUnitIds?.length) {
          onDeliveryUnitsReserved?.(sortOrder, reservedUnitIds);
        }

        notify.error({
          title: positionName,
          body: t('postBookingTool:DELIVERY_UNITS_RESERVED', {
            dates: formatReservedDateRange(dateFrom, dateTo),
            reservedUnitIds: reservedUnitIds?.join(', ') ?? '',
            remoteMailingOrderId: reservedByOrderId?.toString() ?? '',
          }),
        });
        return;
      }

      notify.error({ title: positionName, body: t(translationKey) });
    });

    return true;
  });
};

/**
 * `onFail` handler for requests that may fail per mailing position: shows the per-position errors when the backend
 * sends them, and falls back to the default request error notification otherwise.
 */
export const useMailingRequestErrorNotification = (): RequestErrorNotification => {
  const notifyMailingPositionErrors = useMailingPositionErrorsNotification();
  const notifyRequestError = useRequestErrorNotification();

  return useCallbackRef<RequestErrorNotification>((apiError, isRetrying) => {
    if (notifyMailingPositionErrors(apiError)) {
      return;
    }

    notifyRequestError(apiError, isRetrying);
  });
};
